<template>
  <f7-page class="main-frm-repair-parts-sign">
    <template #fixed>
      <TheNavbar
        :nav-title="navTitle"
        :back-btn-enable="canGoBack"
        class="at-navbar secondary-navbar"
      />
    </template>

    <!-- Preview PDF and Sign Component -->
    <PCSignPdf
      class="pc-parts-preview-pdf-and-sign"
      :pdf="pdf"
      :show-sign-now="signType !== SignTypes.DELIVERY_NOTE_NO_SIGN"
      :show-sign-later="signType !== SignTypes.DELIVERY_NOTE_NO_SIGN"
      :popup-title="navTitle"
      :show-emails-search="signType === SignTypes.DELIVERY_NOTE"
      :show-emails-input="signType === SignTypes.DELIVERY_NOTE"
      :show-nif-input="true"
      :emails="deliveryNoteEmails"
      @sign-later-click="navigateBack"
      @confirm-signature="onSignatureConfirm"
    />
  </f7-page>
</template>

<script setup>
// @ts-check

import { computed, onMounted, ref } from 'vue';
import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import {
  callGetRepairsDeliveryNoteReport,
  callGetRepairsReport,
  callSendDeliveryNoteEmail,
  callSignDeliveryNote,
  callSignPart,
} from '@/js/services/repairs/ApiRepairs';
import { SignTypes } from '@/js/enums/externalRepairParts';
import { f7 } from 'framework7-vue';
import { logError } from '@/js/utils/log';
import { convertBase64ToArrayBuffer } from '@/js/utils/convertBase64ToArrayBuffer';
import { useRepairParts } from '@/composables/useRepairParts';

import TheNavbar from '@/components/TheNavbar.vue';
import PCSignPdf from '@/components/common/PCSignPdf.vue';

/** @import { Ref } from 'vue' */

const props = defineProps({
  /** Stores the id of the selected work part */
  workPartId: { type: Number, required: true },
  /** Stores the current sign type */
  signType: { type: Number, default: 0 },
  /** Stores if user can go back */
  canGoBack: { type: Boolean, default: true },
});

/** @type {Ref<string | ArrayBuffer | undefined>} */
const pdf = ref(undefined);
/** @type {Ref<number | null>} */
const deliveryNoteId = ref(null);
/** @type {Ref<string[] | undefined>} */
const deliveryNoteEmails = ref(undefined);

const { loadWorkParts } = useRepairParts();

const navTitle = computed(() => {
  switch (props.signType) {
    case SignTypes.PART:
      return 'Firmar el parte de reparación';
    case SignTypes.DELIVERY_NOTE:
      return 'Firmar el albarán';
    default:
      return 'Albarán';
  }
});

/** A method that is called when user navigates back (after signing or just going back) */
function navigateBack(signed = false) {
  if (props.canGoBack) {
    f7.views.main.router.back();
  } else {
    // Navigate to main page
    f7.views.main.router.navigate('/repairs/repairParts');
  }

  if (signed) {
    loadWorkParts();
  }
}

/** A method that is called when page is opened with type 'Part' */
async function getRepairsReport() {
  HiDialog.Preloader(true, 'Obteniendo el informe del parte...');

  try {
    const { data } = await callGetRepairsReport(props.workPartId);

    if (data.Valor === true) {
      pdf.value = convertBase64ToArrayBuffer(data.Mensaje ?? '');
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido obtener el informe del parte');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido obtener el informe del parte');
  } finally {
    HiDialog.Preloader(false);
  }
}

/**
 * A method that is called when page is opened with type 'DeliveryNote'
 * @param {boolean} fetchEmails - If the list of emails should be fetched.
 */
async function getRepairsDeliveryNoteReport(fetchEmails) {
  HiDialog.Preloader(true, 'Obteniendo el informe del albarán...');

  try {
    const { data } = await callGetRepairsDeliveryNoteReport(
      props.workPartId,
      fetchEmails,
    );

    if (data.Valor === true) {
      pdf.value = convertBase64ToArrayBuffer(data.Mensaje ?? '');
      deliveryNoteId.value = Number(data.Codigo);

      if (Array.isArray(data.CorreosElectronicos)) {
        deliveryNoteEmails.value = data.CorreosElectronicos.filter((/** @type {string} */ email) => email.length > 0);
      }
    } else {
      HiNotification.Toast('No se ha podido obtener el informe del albarán');

      if (props.signType === SignTypes.DELIVERY_NOTE_NO_SIGN) {
        navigateBack();
      }
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido obtener el informe del albarán');
  } finally {
    HiDialog.Preloader(false);
  }
}

/**
 * A method that is called when user confirmer signing the part.
 * @param {string} signature - The signature to sign.
 */
async function signPart(signature) {
  HiDialog.Preloader(true, 'Firmando el parte...');

  try {
    const { data } = await callSignPart(
      props.workPartId,
      signature,
    );

    if (data.Valor === true) {
      HiNotification.Toast('El parte se ha firmado correctamente');
      navigateBack(true);
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido firmar el parte');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido firmar el parte');
  } finally {
    HiDialog.Preloader(false);
  }
}

/**
 * A method that is called when user confirmer signing the delivery note.
 * @param {string} signature - The signature to sign.
 */
async function signDeliveryNote(signature) {
  if (!deliveryNoteId.value) {
    return false;
  }

  let successful = false;
  HiDialog.Preloader(true, 'Firmando el albarán...');

  try {
    const { data } = await callSignDeliveryNote(
      deliveryNoteId.value,
      signature,
    );

    if (data.Valor === true) {
      successful = true;
      HiNotification.Toast('El albarán se ha firmado correctamente');
    } else {
      HiNotification.Toast(data.Mensaje || 'No se ha podido firmar el albarán');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se ha podido firmar el albarán');
  } finally {
    HiDialog.Preloader(false);
  }

  return successful;
}

/**
 * A method that is used to send emails.
 * @param {any[]} emails - The emails to send.
 * @param {boolean} modified - If the list of emails have been modified.
 */
async function sendDeliveryNoteEmail(emails, modified) {
  if (!deliveryNoteId.value) {
    return false;
  }

  let successful = false;
  HiDialog.Preloader(true, 'Enviando los correos electrónicos...');

  try {
    const { data } = await callSendDeliveryNoteEmail(
      props.workPartId,
      deliveryNoteId.value,
      emails,
      modified,
    );

    if (data.Valor === true) {
      successful = true;
      HiNotification.Toast('Los correos electrónicos se han enviado correctamente');
    } else {
      HiNotification.Toast(data.Mensaje || 'No se han podido enviar los correos electrónicos');
    }
  } catch (error) {
    logError(error);
    HiNotification.Toast('No se han podido enviar los correos electrónicos');
  } finally {
    HiDialog.Preloader(false);
  }

  return successful;
}

/**
 * A method that is used to combine emails from a String and an Array
 * into one String with values separated by comma
 * @param {string} string - The string value.
 * @param {any[]} array - The array value.
 */
function combineEmails(string, array) {
  // Check if the string value is empty or not
  const stringValues = string.trim() === '' ? [] : string.split(',');

  const combinedArray = [...stringValues, ...array];
  return combinedArray;
}

/**
 * A method that is called when the signature is confirmed.
 * @param {string} signature
 * @param {string} _signatoryNIF
 * @param {string} emailsInputValue
 * @param {string[]} selectedEmails
 */
async function onSignatureConfirm(signature, _signatoryNIF, emailsInputValue, selectedEmails) {
  const prefix = 'data:image/jpeg;base64,';
  const validSignature = signature.slice(prefix.length);

  if (props.signType === SignTypes.PART) {
    await signPart(validSignature);
  } else if (props.signType === SignTypes.DELIVERY_NOTE) {
    const signSuccess = await signDeliveryNote(validSignature);
    if (!signSuccess) {
      return;
    }

    const emails = combineEmails(emailsInputValue, selectedEmails);

    if (emails.length > 0) {
      const modified = emailsInputValue.length > 0;
      await sendDeliveryNoteEmail(emails, modified);
    }
  }

  navigateBack(true);
}

onMounted(() => {
  if (props.signType === SignTypes.PART) {
    getRepairsReport();
  } else {
    const fetchEmails = props.signType === SignTypes.DELIVERY_NOTE;
    getRepairsDeliveryNoteReport(fetchEmails);
  }
});
</script>

<style scoped lang="scss">
.main-frm-repair-parts-sign {
  // Sign Component is 100% of the page content (Navbar not included)
  .pc-parts-preview-pdf-and-sign {
    width: 100%;
    height: 100%;
  }
}
</style>
